/**
 * One file's row. React.memo with PRIMITIVE props only: a 200-row table must
 * re-render exactly one row when one row changes state, or typing a label near
 * the end of a batch becomes unusable.
 *
 * The thumbnail is the DOWNSCALED blob's object URL, never the original file -
 * painting a 64px preview by decoding a 24MP original is how a bulk page kills
 * a tab. Rows that have not been processed show no <img> at all.
 */
import React from 'react'
import { Button, Input } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, type Tone } from '../../theme/listkit'
import type { RowState } from './uploadQueue'

const STATE_WORD: Record<RowState, string> = {
  queued: 'Queued',
  processing: 'Processing',
  uploading: 'Uploading',
  saving: 'Saving',
  done: 'Done',
  failed: 'Failed',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
}

const STATE_TONE: Record<RowState, Tone> = {
  queued: 'neutral',
  processing: 'jaguar',
  uploading: 'jaguar',
  saving: 'jaguar',
  done: 'green',
  failed: 'red',
  skipped: 'gold',
  cancelled: 'gold',
}

export interface BulkUploadRowProps {
  id: string
  name: string
  size: number
  label: string
  state: RowState
  reason?: string
  previewUrl?: string | null
  metadataRetained?: boolean
  keptOriginal?: boolean
  /** Seconds spent in `uploading`; only rendered past 10. */
  elapsedS?: number
  onLabelChange(id: string, label: string): void
  onKeepAnyway(id: string): void
}

const kb = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

function BulkUploadRowBase(props: BulkUploadRowProps) {
  const { id, name, size, label, state, reason, previewUrl, metadataRetained, keptOriginal, elapsedS } = props
  const editable = state === 'queued'

  return (
    <div
      data-testid={`muaa-bulk-upload-row-${id}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        padding: '10px 0',
        borderTop: '1px solid ' + brand.cardLine,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 8,
          background: brand.paper,
          border: '1px solid ' + brand.cardLine,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : null}
      </div>

      <div style={{ minWidth: 160, flex: '1 1 200px' }}>
        <div
          style={{
            fontFamily: brand.fSans,
            fontWeight: 600,
            fontSize: 12.5,
            color: brand.ink,
            overflowWrap: 'anywhere',
          }}
        >
          {name}
        </div>
        <div style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub }}>{kb(size)}</div>
      </div>

      <div style={{ flex: '1 1 220px', minWidth: 180 }}>
        {editable ? (
          <Input
            data-testid={`muaa-bulk-upload-row-label-${id}`}
            aria-label={`Label for ${name}`}
            defaultValue={label}
            onChange={(e) => props.onLabelChange(id, e.target.value)}
            size="small"
          />
        ) : (
          <div style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}>
            <span
              data-testid={`muaa-bulk-upload-row-label-${id}`}
              aria-label={`Label for ${name}`}
              aria-disabled="true"
            >
              {label}
            </span>
            {state === 'done' ? <span style={{ marginLeft: 8 }}>edit in Photos</span> : null}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', flex: '2 1 260px' }}>
        <span data-testid={`muaa-bulk-upload-row-state-${id}`}>
          <Pill tone={STATE_TONE[state]}>{STATE_WORD[state]}</Pill>
        </span>
        {metadataRetained ? <Pill tone="gold">original file — location data not removed</Pill> : null}
        {keptOriginal ? <Pill tone="gold">kept original (smaller)</Pill> : null}
        {state === 'uploading' && (elapsedS ?? 0) > 10 ? (
          <span style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub }}>
            uploading, {elapsedS}s
          </span>
        ) : null}
        {reason ? (
          <span style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub, overflowWrap: 'anywhere' }}>
            {reason}
          </span>
        ) : null}
        {state === 'skipped' ? (
          <Button size="small" onClick={() => props.onKeepAnyway(id)}>
            Keep anyway
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export const BulkUploadRow = React.memo(BulkUploadRowBase)
